import { Config } from 'config/envConfig';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Table } from 'antd';
import { Input, LoadingView, MoveCreditsPrompt } from '@quyva/shared-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserTie,
    faCreditCard,
    faCog,
    faBuilding,
    faMinus,
    faPlus,
    faEllipsisV,
    faWallet,
    faKey,
    faFilter,
    faClose,
    faInfoCircle,
    faSave,
    faBan,
} from '@fortawesome/free-solid-svg-icons';
import {
    Button,
    NoInteractionView,
    Modal,
    Dropdown,
    alert,
    AlertType,
    TextArea,
} from '@quyva/shared-ui';
import { Toggle } from '@quyva/shared-ui';
import { getUrl } from '../../../pdfDisplay/utils';
import { ApiKeyManagementContent } from '../apiKeySettings';
import SubscriptionDashboard from '../../../SubscriptionDashboard';
import { SearchOutlined } from '@ant-design/icons';
import { isEmailValid, validateWebsiteUrl } from '@quyva/common';

const fmtLocalPrice = (amount, currencyCode) => {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'narrowSymbol',
            maximumFractionDigits: 2,
        }).format(amount);
    } catch { return `${amount} ${currencyCode}`; }
};

const UserRoles = Object.freeze({
    REGULAR: 'regular',
    PARTNER: 'partner',
    RESELLER: 'reseller',
});

const PaymentStatus = Object.freeze({
    FREE: 'Free',
    PREPAID: 'Prepaid',
    UPCOMING: 'Upcoming',
    PENDING: 'Pending',
    PAID: 'Paid',
});

export const OrganizationManagement = ({
    user,
    isSuperAdmin,
    isPartnerOwner,
    isReseller,
    organizations,
    loading,
    fetchOrganizations,
}) => {
    const [supportedCurrencies, setSupportedCurrencies] = useState([]);
    const [editOrgModal, setEditOrgModal] = useState(false);
    const [orgToEdit, setOrgToEdit] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        owner: '',
        industry: '',
        website: '',
        address: '',
        max_org_qry_limit: 0,
        currency: 'USD',
        per_user_cost: 1,
    });
    const [showSubscriptionDashboard, setShowSubscriptionDashboard] =
        useState(false);
    const [selectedOrgForBilling, setSelectedOrgForBilling] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const [filteredInfo, setFilteredInfo] = useState({});
    const [showOrganizationsModal, setShowOrganizationsModal] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [availableOrganizations, setAvailableOrganizations] = useState([]);
    const [partnerOrganizations, setPartnerOrganizations] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
    const isCreatingOrg = useRef(false);

    const initialNewOrgData = {
        name: '',
        owner: '',
        industry: '',
        website: '',
        address: '',
        max_org_qry_limit: 0,
        isPartner: false,
        currency: 'INR',
    };
    const [newOrgData, setNewOrgData] = useState(initialNewOrgData);
    const [ownerSubInfo, setOwnerSubInfo] = useState(null);
    const [moveOwnerCredits, setMoveOwnerCredits] = useState(true);
    const [showOwnerCreditsPrompt, setShowOwnerCreditsPrompt] = useState(false);

    const [orgMembers, setOrgMembers] = useState([]);
    const [orgMembersLoading, setOrgMembersLoading] = useState(false);
    const initialEditFormDataRef = useRef(null);

    const [partnerHasOrgsModal, setPartnerHasOrgsModal] = useState(false);
    const [partnerToToggle, setPartnerToToggle] = useState(null);
    const [showManageSubscriptionModal, setShowManageSubscriptionModal] =
        useState(false);
    const [orgToManageSubscription, setOrgToManageSubscription] =
        useState(null);
    // 'default' shows the billing-type pill + contextual action button;
    // 'confirm' swaps that same modal's content in place for the
    // confirmation step — no stacked popup-on-popup.
    const [manageSubStep, setManageSubStep] = useState('default');
    const [isLoading, setIsLoading] = useState(false);
    const [showOrgApiKeysModal, setShowOrgApiKeysModal] = useState(false);
    const [selectedOrgForApiKeys, setSelectedOrgForApiKeys] = useState(null);
    const [orgListPage, setOrgListPage] = useState(1);

    useEffect(() => {
        axios.get(getUrl(`http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/exchange-rates`))
            .then(res => setSupportedCurrencies(res.data.supported || []))
            .catch(() => {});
    }, []);

    const updateOrganization = async () => {
        try {
            // Validate required fields
            if (!editFormData.name.trim()) {
                alert(AlertType.ERROR, 'Organization name is required');
                return;
            }

            // Validate email
            if (!isEmailValid(editFormData.owner)) {
                alert(AlertType.ERROR, 'Please enter a valid email address');
                return;
            }

            // Validate website if provided
            if (
                editFormData.website &&
                !validateWebsiteUrl(editFormData.website)
            ) {
                alert(
                    AlertType.ERROR,
                    'Please enter a valid website URL (e.g., https://example.com)'
                );
                return;
            }
            // Validate positive numbers
            if (Number(editFormData.max_org_qry_limit) <= 0) {
                alert(
                    AlertType.ERROR,
                    'Query Limit must be greater than 0'
                );
                return;
            }

            const authToken = encodeURIComponent(user.token);

            setIsLoading(true);

            // Check if trying to downgrade a partner or reseller with organizations
            if (
                (orgToEdit.originalRole === UserRoles.PARTNER ||
                    orgToEdit.originalRole === UserRoles.RESELLER) &&
                orgToEdit.role === UserRoles.REGULAR
            ) {
                const url = getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/partner-organizations`
                );
                const response = await axios.get(
                    `${url}?partnerId=${orgToEdit.id}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: authToken,
                        },
                    }
                );

                if (
                    response.data.organizations &&
                    response.data.organizations.length > 0
                ) {
                    setPartnerToToggle({
                        id: orgToEdit.id,
                        currentRole: orgToEdit.originalRole,
                        orgCount: response.data.organizations.length,
                    });
                    setPartnerHasOrgsModal(true);
                    return;
                }
            }

            // Update organization details
            const updateUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/update-organization`
            );
            const response = await axios.post(
                updateUrl,
                {
                    organizationId: orgToEdit.id,
                    ...editFormData,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: authToken,
                    },
                }
            );

            // If the role has changed, update the role in the same request
            if (orgToEdit.originalRole !== orgToEdit.role) {
                const roleUrl = getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/update-organization-role`
                );
                await axios.post(
                    roleUrl,
                    {
                        organizationId: orgToEdit.id,
                        role: orgToEdit.role,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: authToken,
                        },
                    }
                );
            }

            alert(
                AlertType.SUCCESS,
                response.data.message || 'Organization updated successfully'
            );
            await fetchOrganizations();
            setEditOrgModal(false);
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message;

            alert(
                AlertType.ERROR,
                `${errorMsg || 'Failed to update organization'}`
            );
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrgMembers = async (orgId) => {
        setOrgMembersLoading(true);
        try {
            const authToken = encodeURIComponent(user.token);
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization/members?organizationId=${orgId}`
            );
            const res = await axios.get(url, { headers: { Authorization: authToken } });
            setOrgMembers(res.data.members || []);
        } catch {
            setOrgMembers([]);
        } finally {
            setOrgMembersLoading(false);
        }
    };

    const handleEditOrg = (org) => {
        setOrgToEdit({
            ...org,
            originalRole: org.role,
        });
        const formData = {
            name: org.name,
            owner: org.owner,
            industry: org.industry || '',
            website: org.website || '',
            address: org.address || '',
            max_org_qry_limit: org.max_org_qry_limit || 0,
            currency: org.currency || 'USD',
            per_user_cost: org.per_user_cost ?? 1,
        };
        setEditFormData(formData);
        initialEditFormDataRef.current = formData;
        setOrgMembers([]);
        setEditOrgModal(true);
        fetchOrgMembers(org.id);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const getFilteredOrganizations = () => {
        switch (filterType) {
            case 'partners':
                return organizations.filter(
                    (org) => org.role === UserRoles.PARTNER
                );
            case 'non-partners':
                return organizations.filter(
                    (org) => org.role !== UserRoles.PARTNER
                );
            case 'resellers':
                return organizations.filter(
                    (org) => org.role === UserRoles.RESELLER
                );
            case 'other':
                return organizations.filter(
                    (org) =>
                        org.role !== UserRoles.PARTNER &&
                        org.role !== UserRoles.RESELLER
                );
            default:
                return organizations;
        }
    };

    const organizationsColumns = [
        {
            title: 'Organization Name',
            dataIndex: 'name',
            key: 'name',
            filteredValue: filteredInfo.name || null,
            filterIcon: (filtered) => (
                <SearchOutlined
                    style={{ color: filtered ? '#1890ff' : undefined }}
                />
            ),
            filterDropdown: ({
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
            }) => (
                <div style={{ padding: 8 }} className='flex flex-row gap-2'>
                    <input
                        placeholder='Search organization'
                        value={selectedKeys[0]}
                        onChange={(e) => {
                            setSelectedKeys(
                                e.target.value ? [e.target.value] : []
                            );
                            confirm({ closeDropdown: false });
                        }}
                        onPressEnter={() => confirm()}
                        style={{
                            width: 188,
                            height: 32,
                            marginBottom: 0,
                        }}
                        className='p-2 border rounded'
                    />
                    <div className='flex justify-end gap-2'>
                        <Button
                            onClick={() => {
                                clearFilters();
                                confirm();
                            }}
                            type='success'
                            text='Reset'
                            className='h-8'
                        />
                    </div>
                </div>
            ),
            onFilter: (value, record) =>
                record.name.toLowerCase().includes(value.toLowerCase()),
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Owner',
            dataIndex: 'owner',
            key: 'owner',
            filteredValue: filteredInfo.owner || null,
            filterIcon: (filtered) => (
                <SearchOutlined
                    style={{ color: filtered ? '#1890ff' : undefined }}
                />
            ),
            filterDropdown: ({
                setSelectedKeys,
                selectedKeys,
                confirm,
                clearFilters,
            }) => (
                <div style={{ padding: 8 }} className='flex flex-row gap-2'>
                    <Input
                        placeholder='Search owner'
                        value={selectedKeys[0]}
                        onChange={(e) => {
                            setSelectedKeys(
                                e.target.value ? [e.target.value] : []
                            );
                            confirm({ closeDropdown: false });
                        }}
                        onPressEnter={() => confirm()}
                        style={{
                            width: 188,
                            height: 32,
                            marginBottom: 0,
                        }}
                        className='p-2 border rounded'
                    />
                    <div className='flex justify-end gap-2'>
                        <Button
                            onClick={() => {
                                clearFilters();
                                confirm();
                            }}
                            type='success'
                            text='Reset'
                            className='h-8'
                        />
                    </div>
                </div>
            ),
            onFilter: (value, record) =>
                record.owner.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: (
                <span title='1 credit ≈ 1 chat query on standard model'>
                    Credits Usage
                </span>
            ),
            dataIndex: 'queriesRemaining',
            key: 'queriesRemaining',
            render: (_, record) => {
                const maxQueries =
                    Number(record.billingData?.totalQueriesAllowed) || 20000;
                const usedQueries =
                    Number(record.billingData?.totalQueries) || 0;
                const actualCost  = Number(record.billingData?.actualCostTotal) || 0;
                const marginCost  = Number(record.billingData?.marginCostTotal) || 0;

                return (
                    <div className='flex flex-col gap-0.5'>
                        <span title='1 credit ≈ 1 chat query on standard model' style={{ cursor: 'default' }}>
                            {usedQueries.toFixed(2)}/{maxQueries}
                        </span>
                        {isSuperAdmin && usedQueries > 0 && (
                            <span className='text-xs text-texts-secondary300'>
                                Provider: {actualCost.toFixed(1)} · Margin: {marginCost.toFixed(1)}
                            </span>
                        )}
                    </div>
                );
            },
            sorter: (a, b) => {
                const getUsage = (record) => Number(record.totalQueries) || 0;
                return getUsage(a) - getUsage(b);
            },
        },
        {
            title: 'Unbilled Amount',
            dataIndex: 'monthlyUsage',
            key: 'monthlyUsage',
            render: (_, record) => {
                const amount = record.billingData?.monthlyUsage || '';
                const currency = record.billingData?.currency || 'USD';
                const zeroFmt = new Intl.NumberFormat(undefined, {
                    style: 'currency', currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 2,
                }).format(0);
                if (!record.billingData || parseFloat(amount.replace(/[^0-9.,-]/g, '')) === 0) {
                    return zeroFmt;
                }
                return `-${amount}`;
            },
            sorter: (a, b) => {
                const getAmount = (record) => {
                    const amount = parseFloat(
                        record.billingData?.monthlyUsage?.replace(/[^0-9.,-]/g, '')
                    ) || 0;
                    return -amount;
                };
                return getAmount(a) - getAmount(b);
            },
        },
        {
            title: 'Total Billed',
            dataIndex: 'totalBilled',
            key: 'totalBilled',
            render: (_, record) => {
                const currency = record.billingData?.currency || 'USD';
                const zeroFmt = new Intl.NumberFormat(undefined, {
                    style: 'currency', currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 2,
                }).format(0);
                return record.billingData?.totalBilled || zeroFmt;
            },
            sorter: (a, b) => {
                const getAmount = (record) => {
                    return parseFloat(
                        record.billingData?.totalBilled?.replace(/[^0-9.,-]/g, '')
                    ) || 0;
                };
                return getAmount(a) - getAmount(b);
            },
        },
        {
            title: 'Payment Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            filteredValue: filteredInfo.paymentStatus || null,
            filters: [
                { text: 'Paid', value: PaymentStatus.PAID },
                { text: 'Pending', value: PaymentStatus.PENDING },
                { text: 'Upcoming', value: PaymentStatus.UPCOMING },
                { text: 'Prepaid', value: PaymentStatus.PREPAID },
                { text: 'Free', value: PaymentStatus.FREE },
            ],
            onFilter: (value, record) => {
                const status = record.billingData?.paymentStatus || 'N/A';
                return status === value;
            },
            render: (status, record) => (
                <span
                    className={`px-2 py-1 rounded ${
                        !record.billingData?.paymentStatus
                            ? 'bg-texts-red400 text-white'
                            : record.billingData?.paymentStatus === PaymentStatus.FREE
                            ? 'bg-backgrounds-primary100 text-backgrounds-primary500'
                            : [PaymentStatus.PAID, PaymentStatus.PREPAID].includes(record.billingData?.paymentStatus)
                            ? 'bg-green-100 text-green-800'
                            : record.billingData?.paymentStatus === PaymentStatus.UPCOMING
                            ? 'bg-backgrounds-border text-texts-secondary300'
                            : 'bg-backgrounds-settings100 text-texts-secondary'
                    }`}
                >
                    {record.billingData?.paymentStatus || 'Error'}
                </span>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => {
                // Create dropdown list items based on user role and record properties
                const dropdownItems = [];

                // Only show edit button for superAdmin or partnerOwner, and explicitly exclude reseller
                if ((isSuperAdmin || isPartnerOwner) && !isReseller) {
                    dropdownItems.push({
                        icon: faCog,
                        label: 'Edit',
                        action: () => handleEditOrg(record),
                    });
                }

                // Move Billing Detail action outside the previous condition so resellers can see it
                dropdownItems.push({
                    icon: faWallet,
                    label: 'Billing Detail',
                    action: () => {
                        setSelectedOrgForBilling(record);
                        setShowSubscriptionDashboard(true);
                    },
                });

                // Only show Manage Organizations for superAdmin
                if (isSuperAdmin && (record.isPartner || record.isReseller)) {
                    dropdownItems.push({
                        icon: faBuilding,
                        label: 'Manage Organizations',
                        action: () => handleManageOrganizations(record),
                    });
                }

                if (isSuperAdmin) {
                    dropdownItems.push({
                        icon: faCreditCard,
                        label: 'Manage Subscription',
                        action: () => handleManageSubscription(record),
                    });

                    dropdownItems.push({
                        icon: faKey,
                        label: 'API Keys',
                        action: () => {
                            setSelectedOrgForApiKeys(record);
                            setShowOrgApiKeysModal(true);
                        },
                    });
                }

                return (
                    <Dropdown
                        dropDownList={dropdownItems}
                        icon={<FontAwesomeIcon icon={faEllipsisV} />}
                        buttonClassName='px-2 py-1'
                        placement='bottomRight'
                        listTitleText='Actions'
                        backgroundColor='white'
                    />
                );
            },
        },
    ];

    const handleManageOrganizations = async (partner) => {
        setSelectedPartner(partner);
        setShowOrganizationsModal(true);
        await fetchAvailableOrganizations(partner.id);
        await fetchPartnerOrganizations(partner.id);
    };

    const fetchAvailableOrganizations = async (partnerId) => {
        try {
            const authToken = encodeURIComponent(user.token);
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/available-organizations`
            );
            const response = await axios.get(`${url}?partnerId=${partnerId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authToken,
                },
            });
            setAvailableOrganizations(response.data.organizations);
        } catch (error) {
            console.log('Error fetching available organizations:', error);
        }
    };

    const fetchPartnerOrganizations = async (partnerId) => {
        try {
            const authToken = encodeURIComponent(user.token);
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/partner-organizations`
            );
            const response = await axios.get(`${url}?partnerId=${partnerId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authToken,
                },
            });
            setPartnerOrganizations(response.data.organizations);
        } catch (error) {
            console.log('Error fetching partner organizations:', error);
        }
    };

    const addOrganizationToPartner = async (orgId) => {
        try {
            const authToken = encodeURIComponent(user.token);
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/add-organization-to-partner`
            );
            await axios.post(
                url,
                {
                    email: selectedPartner.owner,
                    organizationId: orgId,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: authToken,
                    },
                }
            );
            alert(AlertType.SUCCESS, 'Organization added successfully');
            await fetchAvailableOrganizations(selectedPartner.id);
            await fetchPartnerOrganizations(selectedPartner.id);
        } catch (error) {
            console.log('Error adding organization:', error);
            alert(AlertType.ERROR, 'Failed to add organization to partner');
        }
    };

    const removeOrganizationFromPartner = async (orgId) => {
        try {
            const authToken = encodeURIComponent(user.token);
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/remove-organization-from-partner`
            );
            await axios.post(
                url,
                {
                    partnerId: selectedPartner.id,
                    organizationId: orgId,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: authToken,
                    },
                }
            );
            alert(AlertType.SUCCESS, 'Organization removed successfully');
            await fetchAvailableOrganizations(selectedPartner.id);
            await fetchPartnerOrganizations(selectedPartner.id);
        } catch (error) {
            console.log('Error removing organization:', error);
            alert(
                AlertType.ERROR,
                'Failed to remove organization from partner'
            );
        }
    };

    const handleCreateOrgClick = async () => {
        // Validate required fields
        if (!newOrgData.name.trim()) {
            alert(AlertType.ERROR, 'Organization name is required');
            return;
        }

        // Validate email
        if (!isEmailValid(newOrgData.owner)) {
            alert(AlertType.ERROR, 'Please enter a valid email address');
            return;
        }
        // Validate website if provided
        if (newOrgData.website && !validateWebsiteUrl(newOrgData.website)) {
            alert(
                AlertType.ERROR,
                'Please enter a valid website URL (e.g., https://example.com)'
            );
            return;
        }
        if (
            Number(newOrgData.max_org_qry_limit) <= 0
        ) {
            alert(
                AlertType.ERROR,
                'Query Limit must be greater than 0'
            );
            return;
        }

        setIsLoading(true);
        try {
            const authToken = encodeURIComponent(user.token);
            const subUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/user-active-subscription?email=${encodeURIComponent(newOrgData.owner)}`
            );
            const subRes = await axios.get(subUrl, {
                headers: { Authorization: authToken },
            });
            if (subRes.data.hasActivePlan) {
                setOwnerSubInfo(subRes.data);
                setMoveOwnerCredits(true);
                setShowOwnerCreditsPrompt(true);
                return;
            }
        } catch {
            // subscription check failed — proceed without move
        } finally {
            setIsLoading(false);
        }

        await createOrganization();
    };

    const createOrganization = async () => {
        if (isCreatingOrg.current) return;
        isCreatingOrg.current = true;
        try {
            setIsLoading(true);

            const authToken = encodeURIComponent(user.token);
            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/create-organization`
            );
            const response = await axios.post(
                url,
                {
                    ...newOrgData,
                    moveOwnerCredits: ownerSubInfo?.hasActivePlan ? moveOwnerCredits : false,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: authToken,
                    },
                }
            );

            if (isPartnerOwner) {
                const partnerUrl = getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/add-organization-to-partner`
                );
                await axios.post(
                    partnerUrl,
                    {
                        email: user.email,
                        organizationId: response.data.organizationId,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: authToken,
                        },
                    }
                );
            }

            alert(AlertType.SUCCESS, response.data.message);
            setShowCreateOrgModal(false);

            setNewOrgData(initialNewOrgData);
            setOwnerSubInfo(null);
            setMoveOwnerCredits(true);
            setShowOwnerCreditsPrompt(false);

            await fetchOrganizations();
        } catch (error) {
            console.log('Error creating organization:', error);
            alert(
                AlertType.ERROR,
                error?.response?.data?.message || 'Failed to create organization'
            );
        } finally {
            setIsLoading(false);
            isCreatingOrg.current = false;
        }
    };

    const handleNewOrgInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'owner') {
            setOwnerSubInfo(null);
            setMoveOwnerCredits(true);
            setShowOwnerCreditsPrompt(false);
        }
        setNewOrgData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePartnerStatusChange = (checked) => {
        setNewOrgData((prev) => ({
            ...prev,
            isPartner: checked,
        }));
    };

    const handleManageSubscription = async (org) => {
        setOrgToManageSubscription(org);
        setManageSubStep('default');
        setShowManageSubscriptionModal(true);
    };

    const updateOrganizationSubscription = async (isPostpaid) => {
        try {
            const authToken = encodeURIComponent(user.token);

            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/update-organization-subscription`
            );
            const response = await axios.post(
                url,
                {
                    organizationId: orgToManageSubscription.id,
                    isPostpaid,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: authToken,
                    },
                }
            );

            alert(
                AlertType.SUCCESS,
                response.data.message || 'Subscription updated successfully'
            );
            await fetchOrganizations();
            setShowManageSubscriptionModal(false);
        } catch (error) {
            console.log('Error updating subscription:', error);
            const message = error?.response?.data?.message;
            alert(AlertType.ERROR, message || 'Failed to update subscription');
        }
    };

    const cancelPostpaidSubscription = async () => {
        try {
            const authToken = encodeURIComponent(user.token);

            const url = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/cancel-postpaid-subscription`
            );
            const response = await axios.post(
                url,
                { organizationId: orgToManageSubscription.id },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: authToken,
                    },
                }
            );

            alert(
                AlertType.SUCCESS,
                response.data.message || 'Postpaid subscription cancelled'
            );
            await fetchOrganizations();
            setShowManageSubscriptionModal(false);
        } catch (error) {
            console.log('Error cancelling postpaid subscription:', error);
            const message = error?.response?.data?.message;
            alert(
                AlertType.ERROR,
                message || 'Failed to cancel postpaid subscription'
            );
        }
    };

    // Add website auto-formatting
    const handleWebsiteChange = (e, isNewOrg = false) => {
        const { value } = e.target;
        // Add https:// if protocol is missing
        if (
            value &&
            !value.startsWith('http://') &&
            !value.startsWith('https://')
        ) {
            if (isNewOrg) {
                setNewOrgData((prev) => ({
                    ...prev,
                    website: `https://${value}`,
                }));
            } else {
                setEditFormData((prev) => ({
                    ...prev,
                    website: `https://${value}`,
                }));
            }
        } else {
            if (isNewOrg) {
                handleNewOrgInputChange(e);
            } else {
                handleInputChange(e);
            }
        }
    };

    if (loading) {
        return (
            <NoInteractionView viewText='Loading organization management data...' />
        );
    }

    if (!isSuperAdmin && !isPartnerOwner && !isReseller) {
        return (
            <div className='p-8 text-center'>
                <FontAwesomeIcon
                    icon={faUserTie}
                    size='2x'
                    className='text-icons-primary mb-4'
                />
                <h2 className='text-lg text-base text-texts-secondary300 mb-2'>
                    Limited Access
                </h2>
                <p className='text-texts-secondary300'>
                    You don't have access to this section
                </p>
            </div>
        );
    }

    return (
        <div className='p-4'>
            <button
                id='createOrgButton'
                className='hidden'
                onClick={() => setShowCreateOrgModal(true)}
            />
            <div className='mb-4 flex justify-between items-center'>
                <div className='flex items-center gap-4'>
                    {isSuperAdmin && (
                        <Dropdown
                            icon={<FontAwesomeIcon icon={faFilter} />}
                            dropDownList={[
                                {
                                    label: 'All Organizations',
                                    action: () => setFilterType('all'),
                                    selected: filterType === 'all',
                                },
                                {
                                    label: 'Partners Only',
                                    action: () => setFilterType('partners'),
                                    selected: filterType === 'partners',
                                },
                                {
                                    label: 'Resellers Only',
                                    action: () => setFilterType('resellers'),
                                    selected: filterType === 'resellers',
                                },
                                {
                                    label: 'Other',
                                    action: () => setFilterType('other'),
                                    selected: filterType === 'other',
                                },
                            ]}
                            buttonText={
                                filterType === 'all'
                                    ? 'All Organizations'
                                    : filterType === 'partners'
                                      ? 'Partners Only'
                                      : filterType === 'resellers'
                                        ? 'Resellers Only'
                                        : 'Other'
                            }
                            showButtonText={true}
                            listTitleText='Filter Organizations'
                            placement='bottomLeft'
                            backgroundColor='white'
                            buttonClassName='w-48'
                        />
                    )}
                </div>
            </div>

            <div className='w-full'>
                <Table
                    dataSource={getFilteredOrganizations()}
                    columns={organizationsColumns}
                    onChange={(pagination, filters, sorter) => {
                        setFilteredInfo(filters);
                    }}
                    rowKey='id'
                    pagination={{ pageSize: 10, current: orgListPage, onChange: setOrgListPage }}
                />
            </div>

            {editOrgModal && orgToEdit && (
                <Modal
                    title='Edit Organization'
                    titleIcon={faUserTie}
                    iconColor='blue'
                    onClose={() => setEditOrgModal(false)}
                    className='w-[90vw] max-w-[800px]'
                >
                    {isLoading ? (
                        <div className='flex items-center h-[54vh] justify-center'>
                            <LoadingView loadingText='Updating organization...' />
                        </div>
                    ) : (
                        <div className='p-4 space-y-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Organization Name
                                    </label>
                                    <Input
                                        type='text'
                                        name='name'
                                        value={editFormData.name}
                                        onChange={(e) =>
                                            handleInputChange({
                                                target: {
                                                    name: 'name',
                                                    value: e.target.value,
                                                },
                                            })
                                        }
                                        placeholder='Required'
                                        className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                    />
                                </div>

                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Owner Email
                                        <FontAwesomeIcon
                                            icon={faInfoCircle}
                                            title='Transfer ownership to an existing member of the organization'
                                            className='ml-2 cursor-pointer text-texts-tertiary'
                                        />
                                    </label>
                                    {orgMembersLoading ? (
                                        <div className='text-sm text-texts-secondary300 px-3 py-2.5 border rounded-xl'>
                                            Loading members…
                                        </div>
                                    ) : orgMembers.length > 0 ? (
                                        <Dropdown
                                            dropDownList={orgMembers.map((m) => ({
                                                label: m.email + (m.is_owner ? ' (Owner)' : m.access_rights === 'admin' ? ' (Admin)' : ''),
                                                action: () => handleInputChange({ target: { name: 'owner', value: m.email } }),
                                                selected: editFormData.owner === m.email,
                                            }))}
                                            showButtonText
                                            buttonText={editFormData.owner || 'Select member'}
                                            listTitleText='Select Owner'
                                            placement='bottomLeft'
                                            customClass={true}
                                            buttonClassName='w-full !rounded-xl font-semibold flex justify-start items-center px-3 py-2.5 border backgrounds-border shadow-inner outline-none text-texts-tertiary bg-backgrounds-white hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200 cursor-pointer text-base truncate'
                                        />
                                    ) : (
                                        <Input
                                            type='email'
                                            name='owner'
                                            value={editFormData.owner}
                                            onChange={(e) =>
                                                handleInputChange({
                                                    target: {
                                                        name: 'owner',
                                                        value: e.target.value,
                                                    },
                                                })
                                            }
                                            placeholder='Required'
                                            className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                        />
                                    )}
                                </div>

                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Industry
                                    </label>
                                    <Input
                                        type='text'
                                        name='industry'
                                        value={editFormData.industry}
                                        onChange={(e) =>
                                            handleInputChange({
                                                target: {
                                                    name: 'industry',
                                                    value: e.target.value,
                                                },
                                            })
                                        }
                                        placeholder='Optional'
                                        className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                    />
                                </div>

                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Website
                                    </label>
                                    <Input
                                        type='url'
                                        name='website'
                                        value={editFormData.website}
                                        onChange={(e) =>
                                            handleWebsiteChange({
                                                target: {
                                                    name: 'website',
                                                    value: e.target.value,
                                                },
                                            })
                                        }
                                        placeholder='Optional - https://example.com'
                                        className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                    />
                                </div>

                                {orgToEdit.is_postpaid && (
                                    <div className='grid grid-cols-2 gap-4'>
                                        <div>
                                            <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                                Organization Maximum Query Limit
                                            </label>
                                            <Input
                                                type='number'
                                                name='max_org_qry_limit'
                                                value={
                                                    editFormData.max_org_qry_limit
                                                }
                                                onChange={(e) =>
                                                    handleInputChange({
                                                        target: {
                                                            name: 'max_org_qry_limit',
                                                            value: e.target.value,
                                                        },
                                                    })
                                                }
                                                className='w-full rounded-xl border p-3 focus:outline-backgrounds-primary300'
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                    Address
                                </label>
                                <TextArea
                                    value={editFormData.address}
                                    onChange={(e) =>
                                        handleInputChange({
                                            target: {
                                                name: 'address',
                                                value: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder='Optional'
                                    rows={3}
                                />
                            </div>

                            {orgToEdit.is_postpaid && (
                                <div className='grid grid-cols-2 gap-4'>
                                    <div>
                                        <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                            Billing Currency
                                        </label>
                                        <Dropdown
                                            dropDownList={supportedCurrencies.map((c) => ({
                                                label: c,
                                                action: () => setEditFormData((prev) => ({ ...prev, currency: c })),
                                                selected: editFormData.currency === c,
                                            }))}
                                            buttonText={editFormData.currency}
                                            showButtonText={true}
                                            listTitleText='Billing Currency'
                                            placement='bottomRight'
                                            customClass={true}
                                            buttonClassName='w-full !rounded-xl font-semibold flex justify-start items-center px-3 py-2.5 border backgrounds-border shadow-inner outline-none text-texts-tertiary bg-backgrounds-white hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200 cursor-pointer text-base'
                                        />
                                    </div>
                                    <div>
                                        <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                            Per User Cost / Month
                                        </label>
                                        <Input
                                            type='number'
                                            name='per_user_cost'
                                            value={editFormData.per_user_cost}
                                            onChange={(e) =>
                                                handleInputChange({
                                                    target: {
                                                        name: 'per_user_cost',
                                                        value: e.target.value,
                                                    },
                                                })
                                            }
                                            placeholder={`Amount in ${editFormData.currency}`}
                                            className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                        />
                                    </div>
                                </div>
                            )}

                            {isSuperAdmin && (
                                <div className='flex flex-col flex-wrap gap-2'>
                                    <div className='flex items-center gap-3'>
                                        <label className='block text-sm text-base text-texts-secondary300 font-semibold'>
                                            Organization Role
                                        </label>
                                        <Dropdown
                                            dropDownList={[
                                                {
                                                    label: 'Regular',
                                                    action: () => {
                                                        setOrgToEdit(
                                                            (prev) => ({
                                                                ...prev,
                                                                role: UserRoles.REGULAR,
                                                                isPartner: false,
                                                                isReseller: false,
                                                            })
                                                        );
                                                    },
                                                    selected:
                                                        orgToEdit.role ===
                                                        UserRoles.REGULAR,
                                                },
                                                {
                                                    label: 'Partner',
                                                    action: () => {
                                                        setOrgToEdit(
                                                            (prev) => ({
                                                                ...prev,
                                                                role: UserRoles.PARTNER,
                                                                isPartner: true,
                                                                isReseller: false,
                                                            })
                                                        );
                                                    },
                                                    selected:
                                                        orgToEdit.role ===
                                                        UserRoles.PARTNER,
                                                },
                                                {
                                                    label: 'Reseller',
                                                    action: () => {
                                                        setOrgToEdit(
                                                            (prev) => ({
                                                                ...prev,
                                                                role: UserRoles.RESELLER,
                                                                isPartner: false,
                                                                isReseller: true,
                                                            })
                                                        );
                                                    },
                                                    selected:
                                                        orgToEdit.role ===
                                                        UserRoles.RESELLER,
                                                },
                                            ]}
                                            buttonText={
                                                orgToEdit.role
                                                    ? orgToEdit.role
                                                          .charAt(0)
                                                          .toUpperCase() +
                                                      orgToEdit.role.slice(1)
                                                    : 'Regular'
                                            }
                                            showButtonText={true}
                                            listTitleText='Select Role'
                                            placement='bottomLeft'
                                            backgroundColor='white'
                                            buttonClassName='w-36'
                                        />
                                    </div>
                                </div>
                            )}

                            {(() => {
                                const hasChanges =
                                    JSON.stringify(editFormData) !== JSON.stringify(initialEditFormDataRef.current) ||
                                    orgToEdit?.role !== orgToEdit?.originalRole;
                                return (
                                    <div className='flex justify-end mt-4'>
                                        <Button
                                            icon={faClose}
                                            text='Cancel'
                                            onClick={() => setEditOrgModal(false)}
                                            type='danger'
                                            className='mr-4'
                                            disabled={!hasChanges}
                                        />
                                        <Button
                                            icon={faSave}
                                            text='Save'
                                            onClick={updateOrganization}
                                            type='success'
                                            disabled={!hasChanges}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </Modal>
            )}

            {showSubscriptionDashboard && selectedOrgForBilling && (
                <Modal
                    title='Billing Detail'
                    titleIcon={faCreditCard}
                    iconColor='blue'
                    onClose={() => {
                        setShowSubscriptionDashboard(false);
                        setSelectedOrgForBilling(null);
                    }}
                    className='w-[95vw] max-w-[1200px] h-[90vh]'
                >
                    <div className='h-full overflow-auto'>
                        <SubscriptionDashboard
                            organizationName={selectedOrgForBilling.name}
                            organizationId={selectedOrgForBilling.id}
                            organizationOwner={selectedOrgForBilling.owner}
                            setManageSubscriptionModal={
                                setShowSubscriptionDashboard
                            }
                        />
                    </div>
                </Modal>
            )}

            {showOrganizationsModal && selectedPartner && (
                <Modal
                    title='Manage Organizations'
                    titleIcon={faBuilding}
                    iconColor='blue'
                    onClose={() => setShowOrganizationsModal(false)}
                    className='w-[90vw] max-w-[1200px]'
                >
                    <div className='p-4 space-y-6'>
                        <div className='flex justify-between items-center mb-4'>
                            <h3 className='text-lg font-semibold'>
                                Partner Organizations
                            </h3>
                            <Input
                                placeholder='Search by name or email'
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 300 }}
                            />
                        </div>

                        <Table
                            dataSource={partnerOrganizations}
                            columns={[
                                {
                                    title: 'Organization Name',
                                    dataIndex: 'name',
                                    key: 'name',
                                    width: '40%',
                                    filteredValue: searchText
                                        ? [searchText]
                                        : null,
                                    onFilter: (value, record) =>
                                        record.name
                                            .toLowerCase()
                                            .includes(value.toLowerCase()) ||
                                        record.owner
                                            .toLowerCase()
                                            .includes(value.toLowerCase()),
                                },
                                {
                                    title: 'Owner Email',
                                    dataIndex: 'owner',
                                    key: 'owner',
                                    width: '40%',
                                },
                                {
                                    title: 'Actions',
                                    key: 'actions',
                                    width: '20%',
                                    render: (_, record) => (
                                        <Button
                                            icon={faMinus}
                                            text='Remove'
                                            onClick={() =>
                                                removeOrganizationFromPartner(
                                                    record.id
                                                )
                                            }
                                            type='danger'
                                        />
                                    ),
                                },
                            ]}
                            pagination={{ pageSize: 5 }}
                        />

                        <h3 className='text-lg font-semibold mt-8'>
                            Available Organizations
                        </h3>
                        <Table
                            dataSource={availableOrganizations}
                            columns={[
                                {
                                    title: 'Organization Name',
                                    dataIndex: 'name',
                                    key: 'name',
                                    width: '40%',
                                    filteredValue: searchText
                                        ? [searchText]
                                        : null,
                                    onFilter: (value, record) =>
                                        record.name
                                            .toLowerCase()
                                            .includes(value.toLowerCase()) ||
                                        record.owner
                                            .toLowerCase()
                                            .includes(value.toLowerCase()),
                                },
                                {
                                    title: 'Owner Email',
                                    dataIndex: 'owner',
                                    key: 'owner',
                                    width: '40%',
                                },
                                {
                                    title: 'Actions',
                                    key: 'actions',
                                    width: '20%',
                                    render: (_, record) => (
                                        <Button
                                            icon={faPlus}
                                            text='Add'
                                            onClick={() =>
                                                addOrganizationToPartner(
                                                    record.id
                                                )
                                            }
                                            type='success'
                                        />
                                    ),
                                },
                            ]}
                            pagination={{ pageSize: 5 }}
                        />
                    </div>
                </Modal>
            )}

            {showCreateOrgModal && (
                <Modal
                    title='Create New Organization'
                    titleIcon={faBuilding}
                    iconColor='green'
                    onClose={() => {
                        setNewOrgData(initialNewOrgData);
                        setOwnerSubInfo(null);
                        setMoveOwnerCredits(true);
                        setShowOwnerCreditsPrompt(false);
                        setShowCreateOrgModal(false);
                    }}
                    className='w-[90vw] max-w-[800px]'
                >
                    {isLoading ? (
                        <div className='flex items-center h-[52vh] justify-center'>
                            <LoadingView loadingText='Creating new organization...' />
                        </div>
                    ) : showOwnerCreditsPrompt && ownerSubInfo ? (
                        <div className='p-6 space-y-6'>
                            <MoveCreditsPrompt
                                activePlan={ownerSubInfo}
                                moveCredits={moveOwnerCredits}
                                onChange={setMoveOwnerCredits}
                                defaultPlanLabel='ENTERPRISE'
                            />
                            <div className='flex justify-end gap-3'>
                                <Button
                                    text='Back'
                                    onClick={() => setShowOwnerCreditsPrompt(false)}
                                    type='secondary'
                                />
                                <Button
                                    icon={faBuilding}
                                    text='Create Organization'
                                    onClick={createOrganization}
                                    type='success'
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className='p-4 space-y-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Organization Name
                                    </label>
                                    <Input
                                        type='text'
                                        name='name'
                                        value={newOrgData.name}
                                        onChange={(e) =>
                                            handleNewOrgInputChange({
                                                target: {
                                                    name: 'name',
                                                    value: e.target.value,
                                                },
                                            })
                                        }
                                        placeholder='Required'
                                        className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                    />
                                </div>

                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Owner Email
                                        <FontAwesomeIcon
                                            icon={faInfoCircle}
                                            title='Owner will be associated with this
                                    organization when they sign up'
                                            className='ml-2 cursor-pointer text-texts-tertiary'
                                        />
                                    </label>
                                    <Input
                                        type='email'
                                        name='owner'
                                        value={newOrgData.owner}
                                        onChange={(e) =>
                                            handleNewOrgInputChange({
                                                target: {
                                                    name: 'owner',
                                                    value: e.target.value,
                                                },
                                            })
                                        }
                                        placeholder='Required - example@company.com'
                                        className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                    />
                                </div>

                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Industry
                                    </label>
                                    <Input
                                        type='text'
                                        name='industry'
                                        value={newOrgData.industry}
                                        onChange={(e) =>
                                            handleNewOrgInputChange({
                                                target: {
                                                    name: 'industry',
                                                    value: e.target.value,
                                                },
                                            })
                                        }
                                        placeholder='Optional'
                                        className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                    />
                                </div>

                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Website
                                    </label>
                                    <Input
                                        type='text'
                                        name='website'
                                        value={newOrgData.website}
                                        onChange={(e) =>
                                            handleWebsiteChange(
                                                {
                                                    target: {
                                                        name: 'website',
                                                        value: e.target.value,
                                                    },
                                                },
                                                true
                                            )
                                        }
                                        placeholder='Optional - https://example.com'
                                        className='w-full p-2.5 rounded-xl border hover:border-backgrounds-primary300 focus:outline-backgrounds-primary200'
                                    />
                                </div>

                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Organization Maximum Query Limit
                                    </label>
                                    <Input
                                        type='number'
                                        name='max_org_qry_limit'
                                        value={String(
                                            newOrgData.max_org_qry_limit
                                        )}
                                        onChange={(e) =>
                                            handleNewOrgInputChange({
                                                target: {
                                                    name: 'max_org_qry_limit',
                                                    value: Number(
                                                        e.target.value
                                                    ),
                                                },
                                            })
                                        }
                                        className='w-full rounded-xl border p-3 focus:outline-backgrounds-primary300'
                                        rows='3'
                                    />
                                </div>
                                <div>
                                    <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                        Billing Currency
                                    </label>
                                    <Dropdown
                                        dropDownList={['INR', 'USD'].map((c) => ({
                                            label: c,
                                            action: () => handleNewOrgInputChange({ target: { name: 'currency', value: c } }),
                                            selected: newOrgData.currency === c,
                                        }))}
                                        buttonText={newOrgData.currency}
                                        showButtonText={true}
                                        listTitleText='Billing Currency'
                                        placement='bottomRight'
                                        customClass={true}
                                        buttonClassName='w-full !rounded-xl font-semibold flex justify-start items-center px-3 py-2.5 border backgrounds-border shadow-inner outline-none text-texts-tertiary bg-backgrounds-white hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200 cursor-pointer text-base'
                                    />
                                </div>
                            </div>

                            <div>
                                <label className='block text-sm text-base text-texts-secondary300 mb-1 font-semibold'>
                                    Address
                                </label>
                                <TextArea
                                    value={newOrgData.address}
                                    onChange={(e) =>
                                        handleNewOrgInputChange({
                                            target: {
                                                name: 'address',
                                                value: e.target.value,
                                            },
                                        })
                                    }
                                    placeholder='Optional'
                                    rows={3}
                                />
                            </div>

                            {isSuperAdmin && (
                                <div className='flex items-center'>
                                    <label className='flex items-center cursor-pointer font-semibold'>
                                        <Toggle
                                            toggleText='Designate as Partner Organization'
                                            toggleTextClassName='text-sm text-texts-secondary300 mr-2'
                                            checked={newOrgData.isPartner}
                                            onChange={handlePartnerStatusChange}
                                            className='mr-2'
                                        />
                                    </label>
                                </div>
                            )}

                            <div className='flex justify-end mt-4'>
                                <Button
                                    icon={faClose}
                                    text='Cancel'
                                    onClick={() => {
                                        setNewOrgData(initialNewOrgData);
                                        setShowCreateOrgModal(false);
                                    }}
                                    type='danger'
                                    className='mr-4'
                                />
                                <Button
                                    icon={faBuilding}
                                    text='Create Organization'
                                    onClick={handleCreateOrgClick}
                                    type='success'
                                    disabled={
                                        isLoading ||
                                        !newOrgData.name ||
                                        !newOrgData.owner
                                    }
                                />
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {partnerHasOrgsModal && partnerToToggle && (
                <Modal
                    title={`Cannot Change ${
                        partnerToToggle.currentRole === UserRoles.RESELLER
                            ? 'Reseller'
                            : 'Partner'
                    } Status`}
                    titleIcon={faBuilding}
                    iconColor='red'
                    onClose={() => {
                        setPartnerHasOrgsModal(false);
                        setPartnerToToggle(null);
                    }}
                    className='w-[500px]'
                >
                    <div className='p-4'>
                        <p className='mb-4'>
                            This {partnerToToggle.currentRole.toLowerCase()} has{' '}
                            {partnerToToggle.orgCount} organization
                            {partnerToToggle.orgCount > 1 ? 's' : ''} associated
                            with it.
                        </p>
                        <p className='mb-6'>
                            Please remove all organizations from this{' '}
                            {partnerToToggle.currentRole.toLowerCase()} before
                            changing its status.
                        </p>
                        <div className='flex justify-end'>
                            <Button
                                text='Understood'
                                onClick={() => {
                                    setPartnerHasOrgsModal(false);
                                    setPartnerToToggle(null);
                                }}
                                type='success'
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {showManageSubscriptionModal && orgToManageSubscription && (
                <Modal
                    title={
                        manageSubStep === 'default'
                            ? 'Manage Subscription'
                            : orgToManageSubscription.is_postpaid
                                ? 'Cancel Postpaid Subscription'
                                : 'Switch to Postpaid Billing'
                    }
                    titleIcon={
                        manageSubStep === 'confirm' &&
                        orgToManageSubscription.is_postpaid
                            ? faBan
                            : faCreditCard
                    }
                    iconColor={
                        manageSubStep === 'confirm' &&
                        orgToManageSubscription.is_postpaid
                            ? 'red'
                            : 'blue'
                    }
                    onClose={() => setShowManageSubscriptionModal(false)}
                    className='w-[500px]'
                >
                    {manageSubStep === 'default' ? (
                        <div className='p-4 space-y-3'>
                            <p className='text-base'>
                                {orgToManageSubscription.name}
                            </p>

                            <div>
                                <span className='block text-xs font-semibold uppercase tracking-wide text-texts-secondary300 mb-2'>
                                    Billing Type
                                </span>
                                {orgToManageSubscription.billingData?.paymentStatus === PaymentStatus.FREE ? (
                                    <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-backgrounds-secondary border border-landing-gray-300 text-texts-secondary300'>
                                        Free
                                    </span>
                                ) : orgToManageSubscription.is_postpaid ? (
                                    <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-backgrounds-primary100 border border-backgrounds-primary200 text-backgrounds-primary400'>
                                        Postpaid
                                    </span>
                                ) : (
                                    <span className='inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-backgrounds-secondary border border-landing-gray-300 text-texts-secondary300'>
                                        Prepaid
                                    </span>
                                )}
                                <p className='text-sm text-texts-secondary300 mt-2'>
                                    {orgToManageSubscription.billingData?.paymentStatus === PaymentStatus.FREE
                                        ? 'Free plan limited credits.'
                                        : orgToManageSubscription.is_postpaid
                                            ? 'Billed monthly based on usage. Cannot be switched back to Prepaid — cancelling falls back to Free.'
                                            : 'Members buy credits directly. No monthly invoice.'}
                                </p>
                            </div>

                            <div className='pt-3 border-t border-landing-gray-200'>
                                {orgToManageSubscription.is_postpaid ? (
                                    <>
                                        <p className='text-sm font-semibold text-texts-secondary200 mb-1'>
                                            Cancel postpaid billing
                                        </p>
                                        <p className='text-sm text-texts-secondary300 mb-3'>
                                            Falls back to the Free plan. Every
                                            member drops to Free plan limits
                                            immediately. A final invoice for
                                            usage up to today is still generated
                                            tonight.
                                        </p>
                                        <Button
                                            icon={faBan}
                                            text='Cancel Postpaid Subscription'
                                            onClick={() =>
                                                setManageSubStep('confirm')
                                            }
                                            type='danger'
                                            className='w-full'
                                        />
                                    </>
                                ) : (
                                    <>
                                        <p className='text-sm font-semibold text-texts-secondary200 mb-1'>
                                            Move to postpaid billing
                                        </p>
                                        <p className='text-sm text-texts-secondary300 mb-3'>
                                            Switches this organization to
                                            Enterprise billing — usage is
                                            invoiced monthly instead of prepaid
                                            credits.
                                        </p>
                                        <Button
                                            icon={faCreditCard}
                                            text='Switch to Postpaid'
                                            onClick={() =>
                                                setManageSubStep('confirm')
                                            }
                                            type='success'
                                            className='w-full'
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    ) : orgToManageSubscription.is_postpaid ? (
                        <div className='p-4'>
                            <p className='mb-4'>
                                Cancel the postpaid plan for{' '}
                                <strong>{orgToManageSubscription.name}</strong> and
                                fall back to Free?
                            </p>
                            <p className='mb-6 text-sm text-texts-secondary300'>
                                Every member of this organization drops to Free
                                plan limits <strong>immediately</strong>. A final
                                invoice for usage up to today will still be
                                generated tonight — this does not write off the
                                current partial billing period.
                            </p>
                            <div className='flex justify-end gap-3'>
                                <Button
                                    text='Keep Postpaid'
                                    type='danger'
                                    onClick={() =>
                                        setManageSubStep('default')
                                    }
                                />
                                <Button
                                    text='Cancel Subscription'
                                    type='success'
                                    onClick={cancelPostpaidSubscription}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className='p-4'>
                            <p className='mb-4'>
                                Move{' '}
                                <strong>{orgToManageSubscription.name}</strong>{' '}
                                to Enterprise billing? Usage from today onward
                                is invoiced monthly instead of{' '}
                                {orgToManageSubscription.billingData?.paymentStatus === PaymentStatus.FREE
                                    ? 'free'
                                    : 'prepaid'}{' '}
                                credits.
                            </p>
                            <p className='mb-6 text-sm text-texts-secondary300'>
                                This cannot be reversed from here — cancelling
                                postpaid later falls back to Free, not Prepaid.
                            </p>
                            <div className='flex justify-end gap-3'>
                                <Button
                                    text={
                                        orgToManageSubscription.billingData?.paymentStatus === PaymentStatus.FREE
                                            ? 'Stay Free'
                                            : 'Stay Prepaid'
                                    }
                                    type='danger'
                                    onClick={() =>
                                        setManageSubStep('default')
                                    }
                                />
                                <Button
                                    text='Switch to Postpaid'
                                    type='success'
                                    onClick={() =>
                                        updateOrganizationSubscription(true)
                                    }
                                />
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {showOrgApiKeysModal && selectedOrgForApiKeys && (
                <Modal
                    title={`API Keys — ${selectedOrgForApiKeys.name}`}
                    titleIcon={faKey}
                    iconColor='sky'
                    onClose={() => {
                        setShowOrgApiKeysModal(false);
                        setSelectedOrgForApiKeys(null);
                    }}
                >
                    <ApiKeyManagementContent orgId={selectedOrgForApiKeys.id} />
                </Modal>
            )}

        </div>
    );
};
